import { Controller } from "@hotwired/stimulus"
import { get, patch } from "@rails/request.js"
import { positionFixedMenu } from "../lib/anchored_menu_position"
import { mountGlModalShell } from "../lib/gl_modal_shell"
import { syncGateReason } from "../lib/gate_reason"

// Card 4 — Notifications.
//
// Two layers of UI:
//   1) Outer panel (this controller's element): event dropdown + master
//      enable/disable toggle + per-channel pills.
//   2) Modal: opened by clicking a channel pill. Tabs let the agent switch
//      between Email / In-App / SMS for the current event. Each tab has its
//      own enable/disable toggle, subject (email only), body textarea, and
//      SMS character counter. All edits auto-sync into the same hidden JSON
//      input — outer "Save notifications" commits to DB.
export default class extends Controller {
  static targets = ["select", "detail", "jsonInput", "goatOnly", "textState"]
  static values = {
    events: Array,
    channels: Array,
    leadSmsEvents: Array,
    crmLocked: Boolean,
    crmLockedReason: String,
    // #17403: { event: { channel: { name, locked, reason } } } — the client
    // messages an active Appointment workflow sends instead
    // (Crm::Workflows::AppointmentCoverage#card_notes).
    workflowCoverage: { type: Object, default: {} },
    channelDefaults: Object,
    state: Object,
    leadTimeUnits: Array,
    multipleReminders: Boolean,
    maxReminders: { type: Number, default: 3 },
    mergeFieldsUrl: String,
  }

  static REMINDER_EVENT_KEY = "reminder"
  static DEFAULT_LEAD_TIME = { value: 15, unit: "minutes" }
  static LEAD_TIME_MIN = 1
  static LEAD_TIME_MAX = 999
  // Several reminders (#15839) — mirrors CalendarConfig::REMINDER_LEAD_TIME_UNIT_MINUTES.
  static LEAD_TIME_UNIT_MINUTES = { minutes: 1, hours: 60, days: 1440 }
  // What "+ Add another reminder" proposes: the first of these not already set.
  static NEW_REMINDER_CANDIDATES = [
    { value: 1, unit: "hours" }, { value: 1, unit: "days" }, { value: 15, unit: "minutes" },
    { value: 30, unit: "minutes" }, { value: 2, unit: "hours" }, { value: 2, unit: "days" },
  ]

  // Events delivered ONLY to the lead — mirrors the server's rule in
  // AppointmentEventNotificationJob#agent_channel_applies? (#10176). Agent
  // channel pills are absent (not disabled) for these: agent email could
  // never apply to a lead-facing thank-you, so a disabled toggle would lie.
  static LEAD_ONLY_EVENT_KEYS = ["follow_up"]
  // Booked events only fire for appointments someone ELSE booked (booking
  // page / AI agent) — mirrors detect_notification_event's externally_booked?
  // gate. The note keeps the card honest for sync-heavy calendars (#10294).
  static BOOKED_EVENT_KEYS = ["appointment_booked_unconfirmed", "appointment_booked_confirmed"]
  // The Lead Email pill on Cancellation is an opt-in for every OTHER
  // appointment; a client who booked through the booking page and received
  // the confirmation is emailed on a GOAT cancel regardless — mirrors
  // AppointmentEventNotificationJob#attendee_cancellation_notice_due? (#14016).
  static CANCELLATION_EVENT_KEY = "cancellation"
  // Per-event "GOAT Appointments Only" (#10313) — mirrors
  // CalendarConfig::GOAT_ONLY_EVENT_KEYS / GOAT_ONLY_DEFAULT_ON_EVENTS.
  // ALL events default checked (owner decision 2026-08-14): the connected
  // calendar already notifies natively for its own events, so synced-event
  // alerts are opt-in per event.
  static GOAT_ONLY_EVENT_KEYS = [
    "appointment_booked_unconfirmed", "appointment_booked_confirmed", "cancellation", "reschedule",
  ]
  static GOAT_ONLY_DEFAULT_ON_KEYS = this.GOAT_ONLY_EVENT_KEYS

  // Toggles persist without the Save button (owner ask, 2026-08-14): an agent
  // shutting off a noisy notification must not lose the change to a missed
  // Save click. Debounced so a burst of toggle flips lands as one PATCH; text
  // edits (templates, redirect URL, lead time) still go through Save.
  static AUTO_SAVE_DEBOUNCE_MS = 600

  connect() {
    this.state = this._normalizeState(this.stateValue)
    this.currentEvent = this.eventsValue[0]?.key
    this.activeChannel = null
    if (this.hasSelectTarget && this.currentEvent) this.selectTarget.value = this.currentEvent
    // Last-persisted text (templates + reminder lead time), used to keep
    // in-flight text edits OUT of toggle auto-saves. Re-captured whenever the
    // full form saves through the Save button.
    this._savedTextState = this._captureTextState()
    this._form = this.element.closest("form")
    this._boundFormSubmitEnd = (event) => {
      if (event.detail?.success) this._savedTextState = this._captureTextState()
    }
    this._form?.addEventListener("turbo:submit-end", this._boundFormSubmitEnd)
    this._renderDetail()
    this._syncJson()
  }

  disconnect() {
    clearTimeout(this._autoSaveTimer)
    this._form?.removeEventListener("turbo:submit-end", this._boundFormSubmitEnd)
    // Turbo can tear the panel down while the editor modal or merge popover is
    // open; the shell's destroy() is idempotent and removes its own listeners.
    if (this.modalShell) this.modalShell.destroy()
    this.modalShell = null
    document.querySelectorAll(".gl-notif-merge-popover").forEach((el) => el.remove())
  }

  // Also the direct action for the Reminder card's server-rendered
  // goat_appointment_reminders_only checkbox (a user attribute on the same
  // form), so checking it saves without the button too.
  autoSave() {
    clearTimeout(this._autoSaveTimer)
    this._autoSaveTimer = setTimeout(() => this._submitToggleState(), this.constructor.AUTO_SAVE_DEBOUNCE_MS)
  }

  // Targeted PATCH, deliberately NOT form.requestSubmit(): the form also
  // carries post_booking_redirect_url and the live-synced hidden JSON with
  // any template text mid-edit, and a whole-form submit would silently
  // persist a half-typed URL or template body the agent never chose to save
  // (review finding, round 2). The payload is the toggle state only — text
  // fields ride at their last-saved values — so the "text goes through Save"
  // invariant survives an auto-save landing mid-keystroke.
  async _submitToggleState() {
    const form = this._form || this.element.closest("form")
    if (!form) return

    const body = new FormData()
    body.append("calendar_config[notification_settings_json]", JSON.stringify(this._stateWithSavedText()))
    const reminders = form.querySelector("input[type=checkbox][name='goat_appointment_reminders_only']")
    if (reminders) body.append("goat_appointment_reminders_only", reminders.checked ? "1" : "0")

    try {
      // turbo-stream response renders the same "Notifications saved." /
      // failure toast the Save button produces.
      await patch(form.action, { body, responseKind: "turbo-stream" })
    } catch (error) {
      console.error("[notification-settings] toggle auto-save failed", error)
    }
  }

  _captureTextState() {
    return Object.fromEntries(Object.entries(this.state).map(([key, entry]) => {
      const saved = { templates: JSON.parse(JSON.stringify(entry.templates || {})) }
      if (entry.lead_time) saved.lead_time = { ...entry.lead_time }
      if (entry.lead_times) saved.lead_times = entry.lead_times.map((lead) => ({ ...lead }))
      return [key, saved]
    }))
  }

  _stateWithSavedText() {
    const out = JSON.parse(JSON.stringify(this.state))
    Object.entries(this._savedTextState || {}).forEach(([key, saved]) => {
      if (!out[key]) return
      out[key].templates = JSON.parse(JSON.stringify(saved.templates))
      if (saved.lead_time) out[key].lead_time = { ...saved.lead_time }
      if (saved.lead_times) out[key].lead_times = saved.lead_times.map((lead) => ({ ...lead }))
    })
    return out
  }

  selectEvent(event) {
    this.currentEvent = event.currentTarget.value
    this._closeModal()
    this._renderDetail()
  }

  toggleMaster(event) {
    const eventKey = event.currentTarget.dataset.event
    if (!this.state[eventKey]) return
    this.state[eventKey].enabled = event.currentTarget.checked
    this._renderDetail()
    this._syncJson()
    this.autoSave()
  }

  // Click on a channel pill: open the modal editor for that channel
  // (which itself owns the per-channel on/off toggle).
  openChannel(event) {
    event.preventDefault()
    const eventKey = event.currentTarget.dataset.event
    const channel = event.currentTarget.dataset.channel
    if (this._channelLocked(channel, eventKey)) return
    this._openModal(eventKey, channel)
  }

  // Text client is a GOAT CRM feature (#15921): an agent without GOAT CRM
  // sees it disabled with the reason, never hidden (owner ruling 2026-10-02).
  // A client message an active Appointment workflow sends instead (#17403)
  // is disabled the same way, naming the workflow. The server skips both at
  // send time regardless.
  _channelLocked(channelKey, eventKey) {
    return this._lockReason(channelKey, eventKey) !== null
  }

  _lockReason(channelKey, eventKey) {
    if (channelKey === "lead_sms" && this.crmLockedValue) return this.crmLockedReasonValue
    const coverage = this._workflowCoverage(channelKey, eventKey)
    return coverage?.locked ? coverage.reason : null
  }

  _workflowCoverage(channelKey, eventKey) {
    return this.workflowCoverageValue?.[eventKey]?.[channelKey] || null
  }

  // ---- Outer panel rendering ----

  _renderDetail() {
    if (!this.hasDetailTarget) return
    const eventKey = this.currentEvent
    const event = this.eventsValue.find((e) => e.key === eventKey)
    const entry = this.state[eventKey] || { enabled: false, channels: {} }
    if (!event) {
      this.detailTarget.innerHTML = ""
      return
    }

    this.detailTarget.innerHTML = ""
    this.detailTarget.appendChild(this._buildHeader(event, entry))
    const isReminder = eventKey === this.constructor.REMINDER_EVENT_KEY
    if (entry.enabled) {
      this.detailTarget.appendChild(this._buildChannels(event, entry))
      if (isReminder) {
        // Owner, 2026-10-07: the agent's own reminder keeps its ONE time;
        // only the client channels take the 1..maxReminders list.
        this.detailTarget.appendChild(this._buildLeadTime(entry))
        if (this.multipleRemindersValue) this.detailTarget.appendChild(this._buildLeadTimes(entry))
      }
      if (this.constructor.GOAT_ONLY_EVENT_KEYS.includes(eventKey)) {
        this.detailTarget.appendChild(this._buildGoatOnlyOption(event, entry))
      }
    } else {
      this.detailTarget.appendChild(this._buildDisabledNotice())
    }

    // The GOAT-only checkbox is server-rendered (it posts a user attribute,
    // not notification JSON) and lives outside detailTarget so innerHTML
    // rebuilds can't destroy it — we only toggle its visibility here.
    if (this.hasGoatOnlyTarget) {
      this.goatOnlyTarget.hidden = !(isReminder && entry.enabled)
    }
  }

  // ---- Reminder lead time (Google Calendar-style "N minutes before") ----

  _buildLeadTime(entry) {
    const lead = entry.lead_time || { ...this.constructor.DEFAULT_LEAD_TIME }
    const wrap = document.createElement("div")
    wrap.className = "gl-notif-lead-time"

    const label = document.createElement("label")
    label.className = "gl-form-label"
    label.setAttribute("for", "gl-notif-lead-time-value")
    label.textContent = this.multipleRemindersValue ? "Remind me" : "Remind"

    const row = document.createElement("div")
    row.className = "gl-notif-lead-time-row"

    const value = document.createElement("input")
    value.type = "number"
    value.id = "gl-notif-lead-time-value"
    value.className = "gl-form-control gl-notif-lead-time-value"
    value.min = String(this.constructor.LEAD_TIME_MIN)
    value.max = String(this.constructor.LEAD_TIME_MAX)
    value.step = "1"
    value.value = String(lead.value)
    value.dataset.action = "input->notification-settings#updateLeadTimeValue"

    const unit = document.createElement("select")
    unit.className = "gl-select gl-notif-lead-time-unit"
    unit.dataset.action = "change->notification-settings#updateLeadTimeUnit"
    this.leadTimeUnitsValue.forEach((u) => {
      const opt = document.createElement("option")
      opt.value = u
      opt.textContent = u
      opt.selected = u === lead.unit
      unit.appendChild(opt)
    })

    const suffix = document.createElement("span")
    suffix.className = "gl-notif-lead-time-suffix"
    suffix.textContent = "before the appointment"

    row.append(value, unit, suffix)
    wrap.append(label, row)
    if (this.multipleRemindersValue) {
      wrap.appendChild(this._buildLeadTimeHelp("Email me, Text me and In-App use this time."))
    }
    return wrap
  }

  _buildLeadTimeHelp(text, extraClass = "") {
    const help = document.createElement("p")
    help.className = `gl-form-hint ${extraClass}`.trim()
    help.textContent = text
    return help
  }

  updateLeadTimeValue(event) {
    const entry = this.state[this.constructor.REMINDER_EVENT_KEY]
    if (!entry) return
    const parsed = parseInt(event.currentTarget.value, 10)
    if (Number.isNaN(parsed)) return // mid-edit blank — keep last valid state
    const clamped = Math.min(Math.max(parsed, this.constructor.LEAD_TIME_MIN), this.constructor.LEAD_TIME_MAX)
    entry.lead_time.value = clamped
    this._syncJson()
  }

  updateLeadTimeUnit(event) {
    const entry = this.state[this.constructor.REMINDER_EVENT_KEY]
    if (!entry) return
    entry.lead_time.unit = event.currentTarget.value
    this._syncJson()
  }

  // ---- Client reminders (#15839): 1..maxReminders rows, one schedule each,
  // for Email client and Text client only (owner, 2026-10-07) ----

  _buildLeadTimes(entry) {
    const leads = entry.lead_times
    const wrap = document.createElement("div")
    wrap.className = "gl-notif-lead-time gl-notif-lead-times"
    wrap.setAttribute("role", "group")
    wrap.setAttribute("aria-labelledby", "gl-notif-lead-times-label")

    const label = document.createElement("p")
    label.className = "gl-form-label"
    label.id = "gl-notif-lead-times-label"
    label.textContent = "Remind clients"
    wrap.appendChild(label)

    leads.forEach((lead, index) => wrap.appendChild(this._buildLeadTimeRow(lead, index, leads.length)))
    wrap.appendChild(this._buildLeadTimeHelp("Email client and Text client are sent at each of these times.",
      "gl-notif-lead-times-help"))

    const error = document.createElement("p")
    error.className = "gl-form-hint text-danger gl-notif-lead-times-error"
    error.id = "gl-notif-lead-times-error"
    error.setAttribute("aria-live", "polite")
    error.textContent = "Two reminders are set for the same time. Change or remove one."
    wrap.appendChild(error)

    if (leads.length < this.maxRemindersValue) {
      const add = document.createElement("button")
      add.type = "button"
      add.className = "gl-btn gl-btn-outline gl-btn-sm gl-notif-lead-time-add"
      add.dataset.action = "notification-settings#addLeadTime"
      add.innerHTML = '<i class="ph ph-plus" aria-hidden="true"></i> Add another reminder'
      wrap.appendChild(add)
    }

    this._markDuplicateLeadTimes(wrap, leads)
    return wrap
  }

  _buildLeadTimeRow(lead, index, count) {
    const position = index + 1
    const row = document.createElement("div")
    row.className = "gl-notif-lead-time-row"
    row.dataset.index = String(index)

    const value = document.createElement("input")
    value.type = "number"
    value.className = "gl-form-control gl-notif-lead-time-value"
    value.min = String(this.constructor.LEAD_TIME_MIN)
    value.max = String(this.constructor.LEAD_TIME_MAX)
    value.step = "1"
    value.value = String(lead.value)
    value.dataset.index = String(index)
    value.setAttribute("aria-label", `Client reminder ${position}: how many`)
    value.dataset.action = "input->notification-settings#updateLeadTimesValue"

    const unit = document.createElement("select")
    unit.className = "gl-select gl-notif-lead-time-unit"
    unit.dataset.index = String(index)
    unit.setAttribute("aria-label", `Client reminder ${position}: unit`)
    unit.dataset.action = "change->notification-settings#updateLeadTimesUnit"
    this.leadTimeUnitsValue.forEach((u) => {
      const opt = document.createElement("option")
      opt.value = u
      opt.textContent = u
      opt.selected = u === lead.unit
      unit.appendChild(opt)
    })

    const suffix = document.createElement("span")
    suffix.className = "gl-notif-lead-time-suffix"
    suffix.textContent = "before the appointment"

    row.append(value, unit, suffix)
    if (count > 1) {
      const remove = document.createElement("button")
      remove.type = "button"
      remove.className = "gl-btn gl-btn-utility gl-btn-sm gl-notif-lead-time-remove"
      remove.dataset.index = String(index)
      remove.dataset.action = "notification-settings#removeLeadTime"
      remove.setAttribute("aria-label", `Remove client reminder ${position}`)
      remove.innerHTML = '<i class="ph ph-x" aria-hidden="true"></i>'
      row.appendChild(remove)
    }
    return row
  }

  updateLeadTimesValue(event) {
    const lead = this._reminderLeadAt(event.currentTarget.dataset.index)
    if (!lead) return
    const parsed = parseInt(event.currentTarget.value, 10)
    if (Number.isNaN(parsed)) return // mid-edit blank — keep last valid state
    lead.value = Math.min(Math.max(parsed, this.constructor.LEAD_TIME_MIN), this.constructor.LEAD_TIME_MAX)
    this._afterLeadTimesEdit()
  }

  updateLeadTimesUnit(event) {
    const lead = this._reminderLeadAt(event.currentTarget.dataset.index)
    if (!lead) return
    lead.unit = event.currentTarget.value
    this._afterLeadTimesEdit()
  }

  addLeadTime() {
    const entry = this.state[this.constructor.REMINDER_EVENT_KEY]
    if (!entry?.lead_times || entry.lead_times.length >= this.maxRemindersValue) return
    const taken = new Set(entry.lead_times.map((lead) => this._leadTimeMinutes(lead)))
    const next = this.constructor.NEW_REMINDER_CANDIDATES.find((c) => !taken.has(this._leadTimeMinutes(c))) ||
      { ...this.constructor.DEFAULT_LEAD_TIME }
    entry.lead_times.push({ ...next })
    this._renderDetail()
    this._syncJson()
    const inputs = this.detailTarget.querySelectorAll(".gl-notif-lead-times .gl-notif-lead-time-value")
    inputs[inputs.length - 1]?.focus()
  }

  removeLeadTime(event) {
    const entry = this.state[this.constructor.REMINDER_EVENT_KEY]
    if (!entry?.lead_times || entry.lead_times.length <= 1) return
    entry.lead_times.splice(Number(event.currentTarget.dataset.index), 1)
    this._renderDetail()
    this._syncJson()
    this.detailTarget.querySelector(".gl-notif-lead-times .gl-notif-lead-time-add, " +
      ".gl-notif-lead-times .gl-notif-lead-time-value")?.focus()
  }

  _reminderLeadAt(index) {
    return this.state[this.constructor.REMINDER_EVENT_KEY]?.lead_times?.[Number(index)]
  }

  _afterLeadTimesEdit() {
    const wrap = this.detailTarget.querySelector(".gl-notif-lead-times")
    if (wrap) this._markDuplicateLeadTimes(wrap, this.state[this.constructor.REMINDER_EVENT_KEY].lead_times)
    this._syncJson()
  }

  _leadTimeMinutes(lead) {
    return lead.value * (this.constructor.LEAD_TIME_UNIT_MINUTES[lead.unit] || 1)
  }

  // Same rule as the server (60 minutes = 1 hour); the server still rejects
  // on Save — this only shows the agent which rows collide.
  _duplicateLeadTimeIndexes(leads) {
    const byMinutes = new Map()
    leads.forEach((lead, index) => {
      const minutes = this._leadTimeMinutes(lead)
      byMinutes.set(minutes, [...(byMinutes.get(minutes) || []), index])
    })
    return new Set([...byMinutes.values()].filter((group) => group.length > 1).flat())
  }

  _markDuplicateLeadTimes(wrap, leads) {
    const duplicates = this._duplicateLeadTimeIndexes(leads)
    wrap.querySelectorAll(".gl-notif-lead-time-row").forEach((row) => {
      const input = row.querySelector(".gl-notif-lead-time-value")
      const isDuplicate = duplicates.has(Number(row.dataset.index))
      input.classList.toggle("is-invalid", isDuplicate)
      input.setAttribute("aria-invalid", String(isDuplicate))
      if (isDuplicate) input.setAttribute("aria-describedby", "gl-notif-lead-times-error")
      else input.removeAttribute("aria-describedby")
    })
    const error = wrap.querySelector(".gl-notif-lead-times-error")
    if (error) error.hidden = duplicates.size === 0
  }

  _buildHeader(event, entry) {
    const wrap = document.createElement("div")
    wrap.className = "gl-notif-header"

    const description = document.createElement("p")
    description.className = "gl-notif-description"
    description.textContent = event.description || ""

    // Canonical design-system switch (label.gl-toggle.gl-toggle--switch):
    // hidden checkbox drives the track/thumb via :has(), matching the React
    // <Toggle> and the HAML forms (audit finding F17).
    const toggleWrap = document.createElement("label")
    toggleWrap.className = "gl-notif-master gl-toggle gl-toggle--switch"

    const cb = document.createElement("input")
    cb.type = "checkbox"
    cb.id = `gl-notif-master-${event.key}`
    cb.checked = entry.enabled === true
    cb.dataset.event = event.key
    cb.dataset.action = "change->notification-settings#toggleMaster"

    const track = document.createElement("div")
    track.className = "gl-toggle-track"
    const thumb = document.createElement("div")
    thumb.className = "gl-toggle-thumb"
    track.append(thumb)

    const labelText = document.createElement("span")
    labelText.className = "gl-toggle-label gl-toggle-label--master"
    labelText.textContent = entry.enabled ? "Notification is enabled" : "Notification is disabled"

    toggleWrap.append(cb, track, labelText)
    wrap.append(description, toggleWrap)
    return wrap
  }

  // Channels that apply to this event: lead-only events drop the agent
  // pills/tabs entirely so the card never shows a toggle that can't fire.
  _channelsForEvent(eventKey) {
    const offersText = this.leadSmsEventsValue.includes(eventKey)
    const channels = this.channelsValue.filter((channel) => channel.key !== "lead_sms" || offersText)
    if (!this.constructor.LEAD_ONLY_EVENT_KEYS.includes(eventKey)) return channels

    return channels.filter((channel) => channel.lead === true)
  }

  _isLeadOnlyEvent(eventKey) {
    return this.constructor.LEAD_ONLY_EVENT_KEYS.includes(eventKey)
  }

  _buildChannels(event, entry) {
    const wrap = document.createElement("div")
    wrap.className = "gl-notif-channels"

    const title = document.createElement("div")
    title.className = "gl-notif-channels-title"
    title.textContent = "Send via"
    wrap.appendChild(title)

    const list = document.createElement("div")
    list.className = "gl-notif-channels-list"

    this._channelsForEvent(event.key).forEach((channel) => {
      const lockReason = this._lockReason(channel.key, event.key)
      const locked = lockReason !== null
      const isOn = !locked && entry.channels?.[channel.key] === true
      const pill = document.createElement("button")
      pill.type = "button"
      pill.className = "gl-notif-channel-pill" +
        (channel.lead ? " gl-notif-channel-pill--lead" : "") +
        (isOn ? " is-active" : "")
      pill.dataset.event = event.key
      pill.dataset.channel = channel.key
      pill.setAttribute("aria-pressed", isOn ? "true" : "false")
      if (!locked) pill.dataset.action = "notification-settings#openChannel"

      const icon = document.createElement("i")
      icon.className = "ph " + (locked ? "ph-lock-simple" : this._iconForChannel(channel.key))
      const label = document.createElement("span")
      label.textContent = channel.label

      pill.append(icon, label)
      if (locked) syncGateReason(pill, { ready: false, reason: lockReason })
      list.appendChild(pill)
    })

    wrap.appendChild(list)
    this._buildWorkflowNotes(event.key).forEach((note) => wrap.appendChild(note))

    if (this._isLeadOnlyEvent(event.key)) {
      const note = document.createElement("p")
      note.className = "gl-notif-description"
      note.textContent = "This notification is sent to your lead by email — you don't receive a copy."
      wrap.appendChild(note)
    }

    if (this.constructor.BOOKED_EVENT_KEYS.includes(event.key)) {
      const note = document.createElement("p")
      note.className = "gl-notif-description"
      note.textContent = "Sent when someone books with you through your booking page or the AI agent — " +
        "appointments you create yourself never trigger booking alerts."
      wrap.appendChild(note)
    }

    if (event.key === this.constructor.CANCELLATION_EVENT_KEY) {
      const note = document.createElement("p")
      note.className = "gl-notif-description"
      note.textContent = "Leads who booked through your booking page and received a confirmation are " +
        `always emailed when you cancel in GOAT — ${this._channelLabel("lead_email")} here covers your other appointments.`
      wrap.appendChild(note)
    }

    return wrap
  }

  // #17403: one line per client channel an Appointment workflow handles.
  _buildWorkflowNotes(eventKey) {
    return Object.entries(this.workflowCoverageValue?.[eventKey] || {}).map(([channelKey, coverage]) => {
      const note = document.createElement("p")
      note.className = "gl-notif-description gl-notif-workflow-note"
      note.textContent = `${this._channelLabel(channelKey)}: ${coverage.reason}.`
      return note
    })
  }

  // ---- Per-event "GOAT Appointments Only" (#10313) ----
  //
  // JSON-backed twin of the Reminder event's server-rendered checkbox (that
  // one posts a user attribute; these ride the notification JSON per event).
  // .gl-notif-goat-only carries the shared shaded-box styling (light + dark);
  // .gl-notif-goat-only-event is the per-event hook for tests/targeting.
  //
  // A yes/no setting is a form switch, switch first with its label on the right
  // (owner, 2026-10-11, #16504): the same markup shared/toggle_row draws with
  // switch_first, so this JS-built row matches the server-rendered one.
  _buildGoatOnlyOption(event, entry) {
    const wrap = document.createElement("div")
    wrap.className = "gl-notif-goat-only gl-notif-goat-only-event"

    const row = document.createElement("div")
    row.className = "d-flex align-items-center gap-2"

    const toggle = document.createElement("label")
    toggle.className = "gl-toggle gl-toggle--switch"

    const box = document.createElement("input")
    box.type = "checkbox"
    box.setAttribute("role", "switch")
    box.id = `gl-notif-goat-only-${event.key}`
    box.checked = entry.goat_only === true
    box.dataset.event = event.key
    box.dataset.action = "notification-settings#toggleGoatOnlyEvent"
    const hintId = `${box.id}_description`
    box.setAttribute("aria-describedby", hintId)

    const track = document.createElement("div")
    track.className = "gl-toggle-track"
    track.setAttribute("aria-hidden", "true")
    const thumb = document.createElement("div")
    thumb.className = "gl-toggle-thumb"
    track.appendChild(thumb)
    toggle.append(box, track)

    const text = document.createElement("div")
    const label = document.createElement("label")
    label.className = "gl-form-label mb-0"
    label.setAttribute("for", box.id)
    label.textContent = "GOAT Appointments Only"
    const hint = document.createElement("div")
    hint.className = "gl-form-hint mt-0"
    hint.id = hintId
    hint.textContent = "When on, this alert only fires for appointments created in GOAT — " +
      "events synced from your connected calendar are skipped."
    text.append(label, hint)

    row.append(toggle, text)
    wrap.appendChild(row)
    return wrap
  }

  toggleGoatOnlyEvent(event) {
    const eventKey = event.currentTarget.dataset.event
    if (!this.state[eventKey]) return
    this.state[eventKey].goat_only = event.currentTarget.checked
    this._syncJson()
    this.autoSave()
  }

  _buildDisabledNotice() {
    const div = document.createElement("div")
    div.className = "gl-notif-disabled-notice"
    div.innerHTML = '<i class="ph ph-bell-slash"></i> <span>This notification is turned off. Channels are hidden until you enable it above.</span>'
    return div
  }

  // The label the server sent for a channel ("Email client" with the booking
  // upgrades on, "Lead Email" off) so copy that names a pill always matches it.
  _channelLabel(key) {
    return this.channelsValue.find((channel) => channel.key === key)?.label || key
  }

  _iconForChannel(key) {
    return ({
      email: "ph-envelope",
      in_app: "ph-bell",
      sms: "ph-chat-circle-text",
      lead_email: "ph-paper-plane-tilt",
      lead_sms: "ph-chat-teardrop-text",
    })[key] || "ph-circle"
  }

  // ---- Modal: per-channel template editor ----

  _openModal(eventKey, channel) {
    this.activeChannel = channel
    this._closeModal()

    const event = this.eventsValue.find((e) => e.key === eventKey)
    if (!event) return

    // Shared dynamic gl-modal shell (audit F43) — chrome (backdrop, header,
    // close ×, footer) is the shell's; only the editor content is ours. The
    // shell mounts INSIDE this.element so the content's data-action attributes
    // bind to this controller instance (Stimulus actions are element-scoped);
    // that also keeps a second instance's modal independent of ours.
    this.modalShell = mountGlModalShell({
      idPrefix: "gl-notif-modal",
      title: `Edit ${event.label.toLowerCase()}`,
      headerIcon: "ph ph-bell",
      container: this.element,
      buttons: [{ text: "Close", className: "gl-btn gl-btn-outline", dismiss: true }],
      onMounted: (shell) => {
        // BUTTON-9: the shell mounts inside the card-dirty form, so keep it out
        // of the card's fingerprint. Its channel switches save themselves, and
        // its text edits reach Save through the textState proxy (_syncJson).
        shell.element.dataset.cardDirtyIgnore = ""
        // .gl-notif-modal survives as the CONTENT wrapper class — the tab
        // switcher and merge-field inserter anchor their queries on it.
        const content = document.createElement("div")
        content.className = "gl-notif-modal"
        content.append(this._buildModalInfo(event), this._buildModalTabs(event), this._buildModalBody(event))
        shell.body.appendChild(content)
      },
      onHidden: (shell) => {
        // Guard the replace race: when _openModal swaps editors, the OLD
        // shell's fade-out finishes AFTER the new shell was already assigned.
        // Its late hidden event must only clear its OWN tracking reference —
        // nulling unconditionally would orphan the new, still-open modal
        // (disconnect/_closeModal could no longer reach it).
        if (this.modalShell === shell) this.modalShell = null
        document.querySelectorAll(".gl-notif-merge-popover").forEach((el) => el.remove())
      },
    })
  }

  _closeModal() {
    // hide() is idempotent and no-ops when nothing is open. The shell is
    // tracked per controller instance, so a second instance of this controller
    // (e.g. in a Turbo frame) closing its own modal can't yank ours.
    if (this.modalShell) this.modalShell.hide()
  }

  switchModalTab(event) {
    event.preventDefault()
    this.activeChannel = event.currentTarget.dataset.channel
    const modal = event.currentTarget.closest(".gl-notif-modal")
    if (!modal) return

    // Re-render tabs (active state) + body (channel-specific editor)
    const oldTabs = modal.querySelector(".gl-notif-modal-tabs")
    const oldBody = modal.querySelector(".gl-notif-modal-body")
    const ev = this.eventsValue.find((e) => e.key === this.currentEvent)
    if (!ev) return

    if (oldTabs) oldTabs.replaceWith(this._buildModalTabs(ev))
    if (oldBody) oldBody.replaceWith(this._buildModalBody(ev))
  }

  toggleModalChannel(event) {
    const channel = event.currentTarget.dataset.channel
    const eventKey = this.currentEvent
    if (!this.state[eventKey]) return

    this.state[eventKey].channels[channel] = event.currentTarget.checked
    this._syncJson()
    this.autoSave()

    // Update label inside modal + outer pill state.
    const labelSpan = event.currentTarget.parentElement.querySelector(".gl-notif-modal-toggle-label")
    if (labelSpan) labelSpan.textContent = event.currentTarget.checked ? "Enabled" : "Disabled"
    this._renderDetail()
  }

  updateTemplate(event) {
    const eventKey = this.currentEvent
    const channel = event.currentTarget.dataset.channel
    const field = event.currentTarget.dataset.field // "subject" or "body"
    if (!this.state[eventKey]) return

    this.state[eventKey].templates ||= {}
    this.state[eventKey].templates[channel] ||= {}
    this.state[eventKey].templates[channel][field] = event.currentTarget.value
    this._syncJson()

    // Live SMS counter
    if ((channel === "sms" || channel === "lead_sms") && field === "body") {
      const counter = event.currentTarget.parentElement.querySelector(".gl-notif-sms-counter")
      if (counter) counter.textContent = `${event.currentTarget.value.length} characters`
    }
  }

  // ---- Modal builders (content only — header/footer belong to the shell) ----

  _buildModalInfo(event) {
    // Avoid `innerHTML` interpolation here — `event.description` is currently
    // sourced from the NOTIFICATION_EVENTS model constant and is safe, but
    // building via createElement + textContent removes the XSS surface if a
    // future change ever wires user-controlled text into this slot.
    const info = document.createElement("div")
    info.className = "gl-notif-modal-info"
    const icon = document.createElement("i")
    icon.className = "ph ph-info"
    const desc = document.createElement("span")
    desc.textContent = event.description || ""
    info.append(icon, desc)
    return info
  }

  _buildModalTabs(event) {
    const tabs = document.createElement("div")
    tabs.className = "gl-notif-modal-tabs"

    this._channelsForEvent(event.key).forEach((channel) => {
      const tab = document.createElement("button")
      tab.type = "button"
      tab.className = "gl-notif-modal-tab" + (this.activeChannel === channel.key ? " is-active" : "")
      tab.dataset.channel = channel.key
      tab.dataset.action = "notification-settings#switchModalTab"
      tab.textContent = channel.label
      tabs.appendChild(tab)
    })

    return tabs
  }

  _buildModalBody(event) {
    const body = document.createElement("div")
    body.className = "gl-notif-modal-body"

    const eventKey = event.key
    const channel = this.activeChannel || this._channelsForEvent(eventKey)[0]?.key || "email"
    const entry = this.state[eventKey] || {}
    const channelEnabled = entry.channels?.[channel] === true
    const tmpl = entry.templates?.[channel] || {}

    body.appendChild(this._buildChannelToggle(eventKey, channel, channelEnabled))

    if (channel === "email") {
      body.appendChild(this._buildSubjectField(eventKey, channel, tmpl.subject || ""))
      body.appendChild(this._buildBodyField(eventKey, channel, tmpl.body || "", "Email body"))
    } else if (channel === "lead_email") {
      body.appendChild(this._buildLeadHint())
      body.appendChild(this._buildSubjectField(eventKey, channel, tmpl.subject || ""))
      body.appendChild(this._buildBodyField(eventKey, channel, tmpl.body || "", "Email body (to the lead)"))
    } else if (channel === "lead_sms") {
      body.appendChild(this._buildLeadHint("Texted to the person who booked, from the GOAT texting number. " +
        "\"Reply STOP to opt out.\" is added if your message doesn't mention STOP."))
      body.appendChild(this._buildBodyField(eventKey, channel, tmpl.body || "", "Text message (to the client)", { showSmsCounter: true }))
    } else if (channel === "in_app") {
      body.appendChild(this._buildBodyField(eventKey, channel, tmpl.body || "", "Message"))
    } else if (channel === "sms") {
      body.appendChild(this._buildBodyField(eventKey, channel, tmpl.body || "", "SMS message", { showSmsCounter: true }))
    }

    return body
  }

  // Lead Email is the one channel whose recipient is NOT the agent — say so
  // right in the editor, above the toggle.
  _buildLeadHint(message = "Sent to the appointment's lead/attendee from your sending identity. Merge fields are filled in per appointment.") {
    const hint = document.createElement("div")
    hint.className = "gl-notif-modal-info"
    const icon = document.createElement("i")
    icon.className = "ph ph-paper-plane-tilt"
    const text = document.createElement("span")
    text.textContent = message
    hint.append(icon, text)
    return hint
  }

  _buildChannelToggle(eventKey, channel, enabled) {
    // Same canonical gl-toggle--switch shape as the master toggle. The
    // gl-notif-modal-toggle-label class stays: toggleModalChannel finds this
    // span via parentElement to flip its text without a rebuild.
    const wrap = document.createElement("label")
    wrap.className = "gl-notif-modal-toggle gl-toggle gl-toggle--switch"

    const cb = document.createElement("input")
    cb.type = "checkbox"
    cb.id = `gl-notif-modal-${eventKey}-${channel}`
    cb.checked = enabled
    cb.dataset.channel = channel
    cb.dataset.action = "change->notification-settings#toggleModalChannel"

    const track = document.createElement("div")
    track.className = "gl-toggle-track"
    const thumb = document.createElement("div")
    thumb.className = "gl-toggle-thumb"
    track.append(thumb)

    const label = document.createElement("span")
    label.className = "gl-toggle-label gl-notif-modal-toggle-label"
    label.textContent = enabled ? "Enabled" : "Disabled"

    wrap.append(cb, track, label)
    return wrap
  }

  _buildSubjectField(eventKey, channel, value) {
    const wrap = document.createElement("div")
    wrap.className = "gl-notif-modal-field"

    const label = document.createElement("label")
    label.className = "form-label"
    label.textContent = "Subject"

    const inputWrap = document.createElement("div")
    inputWrap.className = "gl-notif-input-with-tags"

    const input = document.createElement("input")
    input.type = "text"
    input.className = "gl-form-control"
    input.value = value
    input.dataset.channel = channel
    input.dataset.field = "subject"
    input.dataset.action = "input->notification-settings#updateTemplate"
    input.maxLength = 200

    const tagBtn = this._buildTagButton(channel, "subject")

    inputWrap.append(input, tagBtn)
    wrap.append(label, inputWrap)
    return wrap
  }

  _buildBodyField(eventKey, channel, value, labelText, opts = {}) {
    const wrap = document.createElement("div")
    wrap.className = "gl-notif-modal-field"

    const label = document.createElement("label")
    label.className = "form-label"
    label.textContent = labelText

    const inputWrap = document.createElement("div")
    inputWrap.className = "gl-notif-input-with-tags gl-notif-input-with-tags--textarea"

    const ta = document.createElement("textarea")
    ta.className = "gl-form-control gl-notif-modal-body-input"
    ta.rows = (channel === "sms" || channel === "lead_sms") ? 4 : 8
    ta.value = value
    ta.dataset.channel = channel
    ta.dataset.field = "body"
    ta.dataset.action = "input->notification-settings#updateTemplate"

    const tagBtn = this._buildTagButton(channel, "body")

    inputWrap.append(ta, tagBtn)
    wrap.append(label, inputWrap)

    if (opts.showSmsCounter) {
      const counter = document.createElement("div")
      counter.className = "gl-notif-sms-counter"
      counter.textContent = `${value.length} characters`
      wrap.appendChild(counter)
    }

    return wrap
  }

  // ---- Merge-field picker ----

  _buildTagButton(channel, field) {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.className = "gl-notif-tag-btn"
    btn.title = "Insert merge field"
    btn.setAttribute("aria-label", "Insert merge field")
    btn.dataset.channel = channel
    btn.dataset.field = field
    btn.dataset.action = "notification-settings#openMergeFields"
    btn.innerHTML = '<i class="ph ph-tag"></i>'
    return btn
  }

  openMergeFields(event) {
    event.preventDefault()
    event.stopPropagation()
    const trigger = event.currentTarget
    const channel = trigger.dataset.channel
    const field = trigger.dataset.field

    // Close any existing picker
    document.querySelectorAll(".gl-notif-merge-popover").forEach((el) => el.remove())

    const popover = document.createElement("div")
    popover.className = "gl-notif-merge-popover"
    popover.addEventListener("click", (e) => e.stopPropagation())

    const title = document.createElement("div")
    title.className = "gl-notif-merge-title"
    title.textContent = "Insert field"
    popover.appendChild(title)

    const list = document.createElement("div")
    list.className = "gl-notif-merge-list"
    list.textContent = "Loading…"
    popover.appendChild(list)

    // Anchor to the trigger button, flipping above it near the viewport
    // bottom (right-aligned: these triggers sit at the right edge of the
    // notification rows). Trigger rect is measured before append so the
    // popover's transient in-flow insertion can't shift it.
    const rect = trigger.getBoundingClientRect()
    this.element.appendChild(popover)
    positionFixedMenu(popover, rect, { align: "right" })
    popover.style.zIndex = "var(--gl-zindex-1090)"

    // The field list comes from the registry endpoint (single source with
    // Appointments::MergeFieldRenderer — audit F33). The popover shell mounts
    // and positions synchronously; the list fills when the fetch resolves —
    // and must RE-position then: the real list is far taller than the
    // "Loading…" placeholder the first pass measured, so a popover opened
    // near the viewport bottom would otherwise overflow off-screen.
    this._fetchMergeFieldGroups()
      .then((groups) => {
        if (!popover.isConnected) return
        list.textContent = ""
        this._renderMergeFieldGroups(list, groups, channel, field)
        positionFixedMenu(popover, rect, { align: "right" })
      })
      .catch((error) => {
        // Surface the failure in the console too — the UI message alone hides
        // real backend/network problems from anyone debugging.
        console.error("[notification-settings] merge-field catalog load failed", error)
        if (popover.isConnected) list.textContent = "Couldn't load fields — close and retry."
      })

    // Close on outside click (next tick so this very click doesn't close it).
    const close = (e) => {
      if (!popover.contains(e.target)) {
        popover.remove()
        document.removeEventListener("click", close)
      }
    }
    setTimeout(() => document.addEventListener("click", close), 0)
  }

  insertMergeField(event) {
    event.preventDefault()
    event.stopPropagation()
    const token = event.currentTarget.dataset.token
    const channel = event.currentTarget.dataset.channel
    const field = event.currentTarget.dataset.targetField

    const target = document.querySelector(`.gl-notif-modal [data-channel="${channel}"][data-field="${field}"]`)
    if (!target) return

    const placeholder = `{{${token}}}`
    const start = target.selectionStart ?? target.value.length
    const end = target.selectionEnd ?? target.value.length
    const next = target.value.slice(0, start) + placeholder + target.value.slice(end)
    target.value = next
    target.focus()
    target.setSelectionRange(start + placeholder.length, start + placeholder.length)

    // Manually fire input event so updateTemplate runs and state syncs.
    target.dispatchEvent(new Event("input", { bubbles: true }))

    // Close the popover after insertion.
    document.querySelectorAll(".gl-notif-merge-popover").forEach((el) => el.remove())
  }

  // Fetch-once memoization of the registry's grouped field list
  // ({ categories: [{ category, fields: [{ token, label }] }] }). Memoized
  // only on success so a failed load retries on the next open.
  async _fetchMergeFieldGroups() {
    if (this._mergeFieldGroupsCache) return this._mergeFieldGroupsCache

    const response = await get(this.mergeFieldsUrlValue, { responseKind: "json" })
    if (!response.ok) throw new Error(`merge fields fetch failed (${response.statusCode})`)

    const data = await response.response.json()
    this._mergeFieldGroupsCache = data.categories || []
    return this._mergeFieldGroupsCache
  }

  _renderMergeFieldGroups(list, groups, channel, field) {
    groups.forEach((group) => {
      const heading = document.createElement("div")
      heading.className = "gl-notif-merge-group"
      heading.textContent = group.category
      list.appendChild(heading)

      group.fields.forEach((f) => {
        const item = document.createElement("button")
        item.type = "button"
        item.className = "gl-notif-merge-item"
        item.dataset.token = f.token
        item.dataset.channel = channel
        item.dataset.targetField = field
        item.dataset.action = "notification-settings#insertMergeField"
        item.innerHTML = `<span class="gl-notif-merge-item-label">${f.label}</span><code class="gl-notif-merge-item-token">{{${f.token}}}</code>`
        list.appendChild(item)
      })
    })
  }

  // ---- State ----

  _normalizeState(input) {
    const state = {}
    this.eventsValue.forEach((event) => {
      const stored = (input && typeof input === "object") ? input[event.key] : null
      state[event.key] = {
        enabled: stored?.enabled === true,
        channels: this.channelsValue.reduce((acc, c) => {
          const storedValue = stored?.channels?.[c.key]
          acc[c.key] = typeof storedValue === "boolean"
            ? storedValue
            : this.channelDefaultsValue?.[event.key]?.[c.key] === true
          return acc
        }, {}),
        templates: stored?.templates && typeof stored.templates === "object" ? stored.templates : {},
      }
      if (event.key === this.constructor.REMINDER_EVENT_KEY) {
        state[event.key].lead_time = this._normalizeLeadTime(stored?.lead_time)
        // Only while several reminders are on, so a flag-off save posts exactly today's payload.
        if (this.multipleRemindersValue) {
          state[event.key].lead_times = this._normalizeLeadTimes(stored?.lead_times, state[event.key].lead_time)
        }
      }
      if (this.constructor.GOAT_ONLY_EVENT_KEYS.includes(event.key)) {
        state[event.key].goat_only = typeof stored?.goat_only === "boolean"
          ? stored.goat_only
          : this.constructor.GOAT_ONLY_DEFAULT_ON_KEYS.includes(event.key)
      }
    })
    return state
  }

  _normalizeLeadTime(stored) {
    const fallback = { ...this.constructor.DEFAULT_LEAD_TIME }
    if (!stored || typeof stored !== "object") return fallback
    const value = Number.isInteger(stored.value) &&
      stored.value >= this.constructor.LEAD_TIME_MIN &&
      stored.value <= this.constructor.LEAD_TIME_MAX ? stored.value : fallback.value
    const unit = this.leadTimeUnitsValue.includes(stored.unit) ? stored.unit : fallback.unit
    return { value, unit }
  }

  // A stored list keeps its valid, distinct entries (max maxReminders); a row
  // with no list — every row saved before #15839 — reads its lead_time as one.
  _normalizeLeadTimes(stored, fallback) {
    const list = []
    const seen = new Set()
    const source = Array.isArray(stored) ? stored : []
    source.forEach((raw) => {
      if (list.length >= this.maxRemindersValue || !raw || typeof raw !== "object") return
      const lead = this._normalizeLeadTime(raw)
      const minutes = this._leadTimeMinutes(lead)
      if (seen.has(minutes)) return
      seen.add(minutes)
      list.push(lead)
    })
    return list.length > 0 ? list : [{ ...fallback }]
  }

  _syncJson() {
    if (this.hasJsonInputTarget) this.jsonInputTarget.value = JSON.stringify(this.state)
    this._syncTextState()
  }

  // BUTTON-9: the card's Save commits text (templates, reminder lead times);
  // the toggles save themselves. The textState proxy carries only that text,
  // so card-dirty enables Save for a text edit and never for a toggle or a
  // view switch. A changed value is announced: the editor modal's own fields
  // are card-dirty-ignored, so this proxy is how a template edit reaches it.
  _syncTextState() {
    if (!this.hasTextStateTarget) return
    const next = JSON.stringify(this._captureTextState())
    if (next === this.textStateTarget.value) return
    this.textStateTarget.value = next
    this.textStateTarget.dispatchEvent(new Event("input", { bubbles: true }))
  }
}
